import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { LuxuryButton } from "./LuxuryButton";
import { scrollToId } from "@/lib/scroll";

const PROGRAMMES = [
  {
    id: "foundation",
    name: "Foundation",
    duration: "3-Month Personalised Wellbeing Reset",
    tagline:
      "Crafted with purity, guided by nature's finest — where ancient healing meets refined living.",
    points: [
      "Initial in-depth Ayurvedic consultation",
      "Personalised wellness plan",
      "Three follow-up consultations",
      "Lifestyle and nutrition coaching",
      "WhatsApp support between sessions",
      "Access to monthly group Q&A sessions",
    ],
    note: "Supplements, tests, and any retreat costs are separate.",
    featured: false,
  },
  {
    id: "transformation",
    name: "Transformation",
    duration: "6-Month Personalised Wellbeing Transformation",
    tagline:
      "Rebuild your health, energy, and confidence. Guided Ayurvedic therapies for lasting wellbeing — your best self, naturally.",
    points: [
      "Initial in-depth Ayurvedic consultation",
      "Comprehensive personalised wellness plan",
      "Six follow-up consultations",
      "Ongoing lifestyle and nutrition coaching",
      "WhatsApp support throughout",
      "Access to monthly group Q&A sessions",
      "Progress reviews and plan adjustments",
    ],
    note: "Supplements, tests, and any retreat costs are separate.",
    featured: true,
  },
  {
    id: "post-retreat",
    name: "Post Retreat",
    duration: "9-Month Personalised Wellbeing Reset",
    tagline:
      "Make your transformation last for life — natural therapies, expert follow-up, and a balanced daily routine. Complete holistic care, beyond the retreat.",
    points: [
      "Everything in the 6-Month Transformation",
      "Guidance and preparation for India retreat",
      "Recommended Panchakarma centres",
      "Pre-retreat protocols and support",
      "Post-retreat integration and follow-up",
    ],
    note: "*Plus retreat fee.",
    featured: false,
  },
];

const ProgrammeCard = ({ programme, index }) => (
  <Reveal delay={index * 0.12} className="h-full">
    <div
      data-testid={`programme-card-${programme.id}`}
      className={`relative flex h-full flex-col border p-8 lg:p-10 ${
        programme.featured ? "border-accent bg-accent/5" : "border-border/60 bg-background"
      }`}
    >
      {programme.featured && (
        <span
          data-testid="programme-featured-badge"
          className="absolute -top-3 left-8 bg-accent px-3 py-1 text-[10px] font-medium uppercase tracking-[0.25em] text-accent-foreground"
        >
          Most Chosen
        </span>
      )}
      <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-accent">
        Ayurenew {programme.name}
      </p>
      <h3 className="mt-4 font-serif text-2xl font-normal leading-snug sm:text-3xl">
        {programme.duration}
      </h3>
      <p className="mt-4 text-sm font-light leading-relaxed text-muted-foreground">
        {programme.tagline}
      </p>
      <div className="my-8 h-px bg-border/60" />
      <ul className="flex flex-col gap-4">
        {programme.points.map((pt) => (
          <li key={pt} className="flex items-start gap-3 text-sm font-light leading-relaxed text-foreground/85">
            <Check size={15} className="mt-1 shrink-0 text-accent" />
            <span>{pt}</span>
          </li>
        ))}
      </ul>
      <p className="mt-8 text-xs font-light italic leading-relaxed text-muted-foreground/80">
        {programme.note}
      </p>
      <div className="mt-auto pt-10">
        <LuxuryButton
          data-testid={`programme-cta-${programme.id}`}
          variant={programme.featured ? "brass" : "ghost"}
          className="w-full"
          onClick={() => scrollToId("#contact")}
        >
          Begin with {programme.name}
        </LuxuryButton>
      </div>
    </div>
  </Reveal>
);

export const Programmes = () => (
  <section
    id="programmes"
    data-testid="programmes-section"
    className="border-t border-border/50 py-28 lg:py-40"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal>
        <p
          data-testid="programmes-overline"
          className="text-[11px] font-medium uppercase tracking-[0.4em] text-accent"
        >
          Choose Your Wellbeing Path
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2
          data-testid="programmes-heading"
          className="mt-8 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
        >
          Three paths. One intention — <em className="text-accent">you</em>.
        </h2>
      </Reveal>

      <div className="mt-20 grid gap-8 lg:grid-cols-3 lg:gap-6">
        {PROGRAMMES.map((p, i) => (
          <ProgrammeCard key={p.id} programme={p} index={i} />
        ))}
      </div>

      <Reveal delay={0.15}>
        <p
          data-testid="programmes-note"
          className="mt-16 max-w-3xl text-sm font-light leading-relaxed text-muted-foreground lg:text-base"
        >
          You can join either the 3-month or 6-month programme anytime — before, during or after
          Panchakarma. If this is your very first time, do let us know so we can fully prepare you
          for your wellness journey.
        </p>
      </Reveal>
    </div>
  </section>
);
